/**
 * Staff tasks — the work hub.  MKK adoption Batch 1 (docs/platform-implementation/MKK_ADOPTION_BATCH_1.md §1.1).
 *
 * "Who was going to do that, and is it done?" A task has a title, an optional description, a status, a priority, an
 * optional due DAY (mosque-local, day.js) and an optional assignee. Behind the `tasks` capability, not entitled for any
 * mosque. Two permissions (access.js):
 *   tasks.manage    everything, for every task
 *   work.assigned   my own: change the status of a task assigned to me, and comment on it — nothing else
 *
 * Every change names the version it was based on; a stale one is refused (the API answers 409 with the task as it is
 * now), so two people editing the same task never silently overwrite each other.
 *
 * Pure: no I/O, no clock — "today" is passed in.
 */
const Day = require('./day');

const STATUSES = ['open', 'in_progress', 'done', 'cancelled'];
const ACTIVE = ['open', 'in_progress'];
const PRIORITIES = ['low', 'normal', 'high'];
const MAX_TITLE = 160;
const MAX_DESCRIPTION = 4000;
const MAX_COMMENT = 2000;
const MAX_COMMENTS = 200;
const MAX_TASKS = 5000;
/** Fields a manager may send. `status` is also the one field an assignee may send. */
const FIELDS = ['title', 'description', 'status', 'priority', 'dueOn', 'assigneeId', 'eventId', 'plannerItemId'];
const { ID_RE, isId, CONTROL, cap, oneLine, text } = require('./fields');
const optionalDay = (v, label) => (v === undefined || v === null || v === '' ? { ok: true, value: null }
  : Day.valid(v) ? { ok: true, value: v } : { ok: false, message: `${cap(label)} må være en gyldig dato (ÅÅÅÅ-MM-DD).` });
const optionalId = (v, label) => (v === undefined || v === null || v === '' ? { ok: true, value: null }
  : isId(v) ? { ok: true, value: v } : { ok: false, message: `Ukjent ${label}.` });

/**
 * A task as a manager sends it — every field checked; unknown fields refused. `base` is the task as it is now for a
 * change (PATCH), or nothing for a new one. → { ok, value: {title, description, status, priority, dueOn, assigneeId,
 * eventId, plannerItemId}, changed: [field…] } | { ok: false, errors }
 */
function managerInput(input, base = null) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { task: 'Ugyldig oppgave.' } };
  const unknown = Object.keys(src).filter((k) => !FIELDS.includes(k) && k !== 'version');
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const cur = base || { title: undefined, description: null, status: 'open', priority: 'normal', dueOn: null, assigneeId: null, eventId: null, plannerItemId: null };
  const pick = (k) => (Object.prototype.hasOwnProperty.call(src, k) ? src[k] : cur[k]);
  const errors = {};
  const out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  take('title', oneLine(pick('title'), 'en tittel', MAX_TITLE));
  take('description', text(pick('description'), 'beskrivelsen', MAX_DESCRIPTION));
  const status = pick('status');
  if (!STATUSES.includes(status)) errors.status = 'Ugyldig status.'; else out.status = status;
  const priority = pick('priority');
  if (!PRIORITIES.includes(priority)) errors.priority = 'Velg lav, normal eller høy prioritet.'; else out.priority = priority;
  take('dueOn', optionalDay(pick('dueOn'), 'fristen'));
  take('assigneeId', optionalId(pick('assigneeId'), 'ansvarlig'));
  take('eventId', optionalId(pick('eventId'), 'arrangement'));
  take('plannerItemId', optionalId(pick('plannerItemId'), 'punkt i årshjulet'));
  if (base && !errors.status && base.status !== out.status && !transition(base.status, out.status, 'manager')) {
    errors.status = `Kan ikke gå fra «${STATUS_NO[base.status]}» til «${STATUS_NO[out.status]}».`;
  }
  if (!base && out.status && out.status !== 'open' && out.status !== 'in_progress') errors.status = 'En ny oppgave er åpen eller i gang.';
  if (Object.keys(errors).length) return { ok: false, errors };
  const changed = base ? FIELDS.filter((k) => (base[k] ?? null) !== (out[k] ?? null)) : FIELDS.filter((k) => out[k] != null);
  return { ok: true, value: out, changed };
}

/**
 * The one change an assignee may make to their own task: its status. Anything else in the body is refused, so a
 * volunteer's phone cannot rename, re-date or reassign a task by sending more fields.
 */
function assigneeInput(input, base) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { task: 'Ugyldig oppgave.' } };
  const other = Object.keys(src).filter((k) => k !== 'status' && k !== 'version');
  if (other.length) return { ok: false, errors: Object.fromEntries(other.map((k) => [k, 'Bare status kan endres av den som har oppgaven.'])), reason: 'not_permitted' };
  if (!STATUSES.includes(src.status)) return { ok: false, errors: { status: 'Ugyldig status.' } };
  if (src.status === base.status) return { ok: true, value: { ...base }, changed: [] };
  if (!transition(base.status, src.status, 'assignee')) {
    return { ok: false, errors: { status: `Kan ikke gå fra «${STATUS_NO[base.status]}» til «${STATUS_NO[src.status]}».` }, reason: 'not_permitted' };
  }
  return { ok: true, value: { ...base, status: src.status }, changed: ['status'] };
}

const STATUS_NO = { open: 'Åpen', in_progress: 'I gang', done: 'Ferdig', cancelled: 'Avbrutt' };
const PRIORITY_NO = { low: 'Lav', normal: 'Normal', high: 'Høy' };

/**
 * Whether a status change is allowed, and for whom ('manager' | 'assignee'). Same status is not a transition.
 *   open ↔ in_progress, active → done, done → open: both
 *   active → cancelled, cancelled → open: the manager only
 */
function transition(from, to, who) {
  if (!STATUSES.includes(from) || !STATUSES.includes(to) || from === to) return false;
  if (ACTIVE.includes(from) && ACTIVE.includes(to)) return true;
  if (ACTIVE.includes(from) && to === 'done') return true;
  if (from === 'done' && to === 'open') return true;
  if (who !== 'manager') return false;
  if (ACTIVE.includes(from) && to === 'cancelled') return true;
  if (from === 'cancelled' && to === 'open') return true;
  return false;
}

/** The version rule: a change must name the version it was based on. */
function versionOk(sent, current) { return Number.isSafeInteger(sent) && sent === current; }

function comment(input) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const r = text(src.body, 'en kommentar', MAX_COMMENT, true);
  return r.ok ? { ok: true, value: r.value } : { ok: false, errors: { body: r.message } };
}

/** Where a task stands against today: 'overdue' | 'today' | 'week' (within the next 6 days) | 'later' | 'none'. */
function dueState(task, today) {
  // A stored day that is not a real one (made before the database refused it, or written by hand) is no due date here,
  // never an exception (Batch 1 remediation): the list still answers, and the task says its date is invalid (work-hub).
  if (!task.dueOn || !Day.valid(task.dueOn) || !ACTIVE.includes(task.status)) return 'none';
  const d = Day.diff(today, task.dueOn);
  return d < 0 ? 'overdue' : d === 0 ? 'today' : d <= 6 ? 'week' : 'later';
}

/**
 * The list's filters, from a query string's values. Unknown values are refused (400), never ignored: a filter that
 * silently does nothing shows the wrong list. → { ok, value } | { ok: false, message }
 */
const STATUS_FILTERS = ['active', 'all', ...STATUSES];
const DUE_FILTERS = ['overdue', 'today', 'week', 'none'];
const SORTS = ['due', 'priority', 'updated'];
function filter(q = {}) {
  const get = (k) => (q[k] === undefined || q[k] === null || q[k] === '' ? undefined : String(q[k]));
  const f = { scope: get('scope') || 'mine', status: get('status') || 'active', q: get('q') || '', assignee: get('assignee'),
    due: get('due'), priority: get('priority'), event: get('event'), sort: get('sort') || 'due' };
  if (!['mine', 'all'].includes(f.scope)) return { ok: false, message: 'Ugyldig utvalg.' };
  if (!STATUS_FILTERS.includes(f.status)) return { ok: false, message: 'Ugyldig statusfilter.' };
  if (f.due && !DUE_FILTERS.includes(f.due)) return { ok: false, message: 'Ugyldig fristfilter.' };
  if (f.priority && !PRIORITIES.includes(f.priority)) return { ok: false, message: 'Ugyldig prioritet.' };
  if (f.assignee && f.assignee !== 'none' && !isId(f.assignee)) return { ok: false, message: 'Ukjent ansvarlig.' };
  if (f.event && !isId(f.event)) return { ok: false, message: 'Ukjent arrangement.' };
  if (!SORTS.includes(f.sort)) return { ok: false, message: 'Ugyldig sortering.' };
  if (f.q.length > 100) return { ok: false, message: 'Søket kan ha maks 100 tegn.' };
  f.q = f.q.replace(/\s+/g, ' ').trim().toLowerCase();
  return { ok: true, value: f };
}

/** Whether a task passes a filter (the scope — mine or all — is the caller's: it decides what may be seen at all). */
function matches(task, f, today) {
  if (f.status === 'active' ? !ACTIVE.includes(task.status) : f.status !== 'all' && task.status !== f.status) return false;
  if (f.priority && task.priority !== f.priority) return false;
  if (f.assignee === 'none' ? task.assigneeId : f.assignee && task.assigneeId !== f.assignee) return false;
  if (f.event && task.eventId !== f.event) return false;
  if (f.due) {
    const s = dueState(task, today);
    // 'week' is today and the six days after it. Overdue work is NOT in it (hardening, 2026-10-02): it was, so «Denne
    // uken» showed last month's forgotten task among this week's; overdue has its own filter, chosen on purpose.
    if (f.due === 'none' ? Boolean(task.dueOn) : f.due === 'week' ? !['today', 'week'].includes(s) : s !== f.due) return false;
  }
  if (f.q && !(`${task.title}\n${task.description || ''}`.toLowerCase().includes(f.q))) return false;
  return true;
}

const PRIORITY_RANK = { high: 0, normal: 1, low: 2 };
/**
 * The list's order. 'due': active before finished, then earliest due first (no due date last), then priority, then
 * title. 'priority': high first, then due. 'updated': most recently changed first. Stable and total: ties end on id.
 */
function sort(tasks, by = 'due') {
  const active = (t) => (ACTIVE.includes(t.status) ? 0 : 1);
  const due = (t) => t.dueOn || '9999-12-31';
  const cmp = {
    due: (a, b) => active(a) - active(b) || due(a).localeCompare(due(b)) || PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority],
    priority: (a, b) => active(a) - active(b) || PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || due(a).localeCompare(due(b)),
    updated: (a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')),
  }[by] || (() => 0);
  return [...tasks].sort((a, b) => cmp(a, b) || String(a.title).localeCompare(String(b.title), 'nb') || String(a.id).localeCompare(String(b.id)));
}

/**
 * One page of a list (hardening, 2026-10-02): `limit` (1–200, default 50) and `offset` (from 0), from a query string.
 * Refused, not clamped, when out of range — a list that silently shows a different page is the wrong list.
 * → { ok, value: { limit, offset } } | { ok: false, message }
 */
const PAGE_DEFAULT = 50, PAGE_MAX = 200;
function page(q = {}) {
  const get = (k) => (q[k] === undefined || q[k] === null || q[k] === '' ? undefined : String(q[k]));
  const lim = get('limit'), off = get('offset');
  const limit = lim === undefined ? PAGE_DEFAULT : Number(lim), offset = off === undefined ? 0 : Number(off);
  if (!/^\d+$/.test(lim ?? '1') || !Number.isSafeInteger(limit) || limit < 1 || limit > PAGE_MAX) return { ok: false, message: `Antall per side må være 1–${PAGE_MAX}.` };
  if (!/^\d+$/.test(off ?? '0') || !Number.isSafeInteger(offset) || offset > 100000) return { ok: false, message: 'Ugyldig side.' };
  return { ok: true, value: { limit, offset } };
}
/** A page of an already filtered and sorted list, with where the next one starts (null at the end). */
function slice(list, { limit, offset }) {
  const items = list.slice(offset, offset + limit);
  return { total: list.length, offset, limit, nextOffset: offset + items.length < list.length ? offset + items.length : null, items };
}

module.exports = { PAGE_DEFAULT, PAGE_MAX, page, slice, STATUSES, ACTIVE, PRIORITIES, STATUS_NO, PRIORITY_NO, FIELDS, MAX_TITLE, MAX_DESCRIPTION, MAX_COMMENT, MAX_COMMENTS, MAX_TASKS,
  isId, oneLine, text, CONTROL, cap, managerInput, assigneeInput, transition, versionOk, comment, dueState, filter, matches, sort };
